<template>
  <div>
    <!-- PASSWORD -->
    <form-row
      v-show="!isQuickPassLogin"
      :is-vertical-splitted="false"
    >
      <validation-provider
        ref="passwordValidationProvider"
        v-slot="{ errors }"
        :vid="`${stepId}_user-login-password`"
        name="Password"
        rules="required"
      >
        <qz-text-field
          v-model="loginPassword"
          :error-messages="errors"
          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          :name="`${stepId}_user-login-password`"
          placeholder="Password"
          data-test="new-user-password"
          :type="showPassword ? 'text': 'password'"
          @click:append="showPassword = !showPassword"
        />
      </validation-provider>
    </form-row>
    <!-- ONETIME PASSWORD -->
    <form-row
      v-show="isQuickPassLogin"
      question="Enter your quick passcode"
      :help-text="passcodeHelpText"
      :is-vertical-splitted="false"
    >
      <validation-provider
        ref="passcodeValidationProvider"
        v-slot="{ errors }"
        :vid="`${stepId}_user-login-one-time-password`"
        name="One-time Password"
        rules="required"
      >
        <qz-number-input
          v-model.number="loginPasscode"
          :name="`${stepId}_user-login-one-time-password`"
          :error-messages="errors"
          placeholder="e.g. 123456"
          data-test="new-user-one-time-password"
          :without-separators="true"
          :maxlength="inputCharsLimiters.ONE_TIME_PASSWORD"
        />
        <div class="mt-2 ml-3 text-caption">
          <a
            class="text-decoration-underline"
            :class="{'unclickable': passwordResetTimer > 0}"
            @click.prevent="getNewOneTimePassword"
          >
            Didn't get it? Click here to request a new passcode. {{ passwordResetTimer === 0 ? '' : `(${passwordResetTimer})` }}
          </a>
        </div>
      </validation-provider>
    </form-row>
    <form-row
      v-if="!isQuickPassLogin"
      :is-vertical-splitted="false"
    >
      <qz-btn
        color="navy-light"
        class="text-wrap password-actions"
        rounded
        outlined
        data-test="passcode-by-email"
        x-large
        :loading="loading"
        @click.prevent="quickPassLogin"
      >
        Forgot it? Click here to receive a quick passcode by email
      </qz-btn>
    </form-row>
    <form-row :is-vertical-splitted="false">
      <qz-btn
        color="navy-light"
        class="btn btn-login"
        rounded
        data-test="password-login"
        x-large
        @click.prevent="$emit('login')"
      >
        Login
      </qz-btn>
    </form-row>
    <form-row
      v-if="isQuickPassLogin"
      :is-vertical-splitted="false"
    >
      <qz-btn
        color="navy-light"
        rounded
        outlined
        x-large
        data-test="quick-password-login"
        @click.prevent="passwordLogin"
      >
        Log in with password instead
      </qz-btn>
    </form-row>
  </div>
</template>

<script>
export default {
  name: 'new-user-password',
  inject: [
    'helpers',
    'inputCharsLimiters',
  ],
  props: {
    stepId: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: null,
    },
    isQuickPassLogin: {
      type: Boolean,
      default: false,
    },
    fieldErrors: {
      type: Array,
      default: () => [],
    },
    partner: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      loginPassword: '',
      loginPasscode: '',
      passwordResetTimer: 0,
      passwordResetTimerInterval: null,
      loading: false,
      showPassword: false,
      passcodesSent: 0,
    };
  },
  computed: {
    passcodeHelpText () {
      return this.passcodesSent >= 2
        ? 'We’ve emailed you another code. Please check your spam/junk folder'
        : 'We\'ve emailed your quick passcode. Your code will only be valid for ten minutes. Please also check your spam/junk folder.';
    },
  },
  methods: {
    async getNewOneTimePassword () {
      try {
        if (this.passwordResetTimer === 0) {
          await this.$api.sendPasscode({
            email: this.email,
            partner: this.partner,
          });

          this.passwordResetTimer = 60;
          this.passcodesSent += 1;

          this.passwordResetTimerInterval = setInterval(() => {
            if (this.passwordResetTimer === 0) {
              clearInterval(this.passwordResetTimerInterval);
            } else {
              this.passwordResetTimer--;
            }
          }, 1000);
        }
      } catch (error) {
        console.log(error);
      }
    },
    async quickPassLogin () {
      this.loginPassword = '';
      this.$emit('reset-question');
      this.loading = true;
      await this.getNewOneTimePassword();
      this.$emit('emailed-one-time-password');
      this.loading = false;
    },
    passwordLogin () {
      this.loginPasscode = '';
      this.$emit('login-with-password');
    },
  },
  watch: {
    loginPassword (newPassword) {
      this.$emit('password-changed', newPassword);
    },
    loginPasscode (newPasscode) {
      this.$emit('passcode-changed', newPasscode);
    },
    fieldErrors (newFieldErrors) {
      const errorCodesMap = {
        pass_03: 'Please enter your password',
        pass_02: 'The password is incorrect. Please try again.',
        passc_03: 'Please enter your passcode',
        passc_02: 'The passcode is incorrect. Please try again.',
      };

      ['password', 'passcode'].forEach(field => {
        const errors = newFieldErrors[0][field];

        if (errors) {
          this.$refs[`${field}ValidationProvider`].applyResult({
            errors: errors.map(error => errorCodesMap[error.code]),
            valid: false,
            failedRules: {},
          });
        }
      });
    },
  },
};
</script>

<style lang='scss' scoped>
$clr-grey: #7f7f7f;

.unclickable {
  cursor: not-allowed;
  color: $clr-grey;
}

.password-actions::v-deep {
  .v-btn__content {
    flex: auto;
  }
}
</style>
